import { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  createValidationStatus,
  getValidationStatus,
  updateValidationStatus,
} from '../services/project/validationStatusService';

const NAME_OPTIONS = ['Validated', 'Invalid'];

const schema = Yup.object({
  name: Yup.string()
    .trim()
    .required('Name is required')
    .oneOf(NAME_OPTIONS, 'Name must be either Validated or Invalid'),
  description: Yup.string().trim().max(1000),
});

const EditValidationStatusModal = ({
  show,
  onClose,
  status,
  projectId,
  onSaved = () => {},
}) => {
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: values.name.trim(),
          description: (values.description || '').trim(),
        };

        if (status?.id) {
          const res = await updateValidationStatus(status.id, payload);
          toast.success(res?.message || 'Validation status updated');
        } else {
          const res = await createValidationStatus({
            ...payload,
            projectId: Number(projectId),
          });
          toast.success(res?.message || 'Validation status created');
        }

        onSaved();
        onClose();
      } catch (error) {
        toast.error(error?.response?.data?.errorMessage || 'Something went wrong');
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const hydrateForm = async () => {
      if (!show) return;

      if (!status?.id) {
        formik.setValues({ name: '', description: '' });
        return;
      }

      try {
        setLoading(true);
        const res = await getValidationStatus(status.id);
        const entity = res?.status || res?.data || res;
        const resolvedName = entity?.name || status.name || '';
        formik.setValues({
          name: NAME_OPTIONS.includes(resolvedName) ? resolvedName : '',
          description: entity?.description || status.description || '',
        });
      } catch (error) {
        const fallbackName = status?.name || '';
        formik.setValues({
          name: NAME_OPTIONS.includes(fallbackName) ? fallbackName : '',
          description: status?.description || '',
        });
      } finally {
        setLoading(false);
      }
    };

    hydrateForm();
  }, [show, status?.id]);

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{status?.id ? 'Edit Validation Status' : 'New Validation Status'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <p className="mb-0">Loading...</p>
        ) : (
          <Form onSubmit={formik.handleSubmit}>
            <Form.Group className="mb-3" controlId="validationStatusName">
              <Form.Label>Name</Form.Label>
              <Form.Select
                {...formik.getFieldProps('name')}
                isInvalid={formik.touched.name && !!formik.errors.name}
                disabled={formik.isSubmitting}
              >
                <option value="">Select name</option>
                {NAME_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formik.errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="validationStatusDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter description"
                {...formik.getFieldProps('description')}
                isInvalid={formik.touched.description && !!formik.errors.description}
                disabled={formik.isSubmitting}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.description}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={formik.isSubmitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={formik.isSubmitting || !!formik.errors.name || !!formik.errors.description}
              >
                {formik.isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EditValidationStatusModal;
